import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Button,
  Badge,
  Menu,
  ActionIcon,
  Stack,
  Pagination,
  Modal,
  Select,
  Popover,
  Skeleton,
  TextInput,
  Radio,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconSpeakerphone,
  IconCheck,
  IconRefresh,
  IconEye,
  IconSearch,
  IconFilter,
  IconDownload,
  IconX,
  IconAlertTriangle,
} from "@tabler/icons-react";
import axios from "axios";
import { ExportModal } from "../../../components/ExportModal";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import { HttpCodes } from "../../../lib/data";

export type Campaign = {
  _id: string; // Mongoose ID
  title: string;
  clientName: string;
  description?: string;
  status: "active" | "draft" | "completed";
  createdAt: string;
  endDate?: string;
  minLevel?: number;
  branding: {
    primaryColor: string;
    secondaryColor?: string;
    logoUrl?: string;
    bannerUrl?: string;
  };
  eligibility?: {
    type: "open" | "criteria";
    minMissionCompletions: number;
  };
  completionBonus?: {
    amount: number;
    currency: string;
  };
  isLocked?: boolean;
  lockReason?: string;
  squadsOnly?: boolean;
};

const CampaignActions = ({
  campaign,
  onDelete,
  onUpdateStatus,
}: {
  campaign: Campaign;
  onDelete: (campaign: Campaign) => void;
  onUpdateStatus: (campaign: Campaign) => void;
}) => {
  const navigate = useNavigate();

  return (
    <Group gap={4}>
      <ActionIcon
        variant="light"
        color="red"
        onClick={() => onDelete(campaign)}
      >
        <IconTrash size={16} />
      </ActionIcon>

      <Menu shadow="md" width={200} position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray">
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconEye size={14} />}
            onClick={() => navigate(`/d/campaigns/${campaign._id}`)}
          >
            View Campaign
          </Menu.Item>
          <Menu.Item
            leftSection={<IconEdit size={14} />}
            onClick={() => navigate(`/d/campaigns/update/${campaign._id}`)}
          >
            Edit Campaign
          </Menu.Item>
          <Menu.Item
            leftSection={<IconRefresh size={14} />}
            onClick={() => onUpdateStatus(campaign)}
          >
            Update Status
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);

  useEffect(() => {
    document.title = "Campaigns | Virel Admin";
  }, []);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Status Modal State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null,
  );
  const [newStatus, setNewStatus] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(
    null,
  );
  const [deleteMissions, setDeleteMissions] = useState<string>("keep");
  const [isDeleting, setIsDeleting] = useState(false);

  const endpoint = useMemo(
    () =>
      Endpoints.GetCampaigns({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
      }),
    [currentPage, pageSize, debouncedSearch, statusFilter],
  );

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<Campaign[]>
  >({
    method: endpoint.method,
    route: endpoint.route,
  });

  const campaigns = Array.isArray(data?.data)
    ? data.data
    : Array.isArray((data?.data as any)?.data)
      ? (data?.data as any).data
      : [];
  const totalRecords =
    (data as any)?.totalRecords ?? (data?.data as any)?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));

  const openDeleteModal = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setDeleteMissions("keep");
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!campaignToDelete) return;
    setIsDeleting(true);

    const ep = Endpoints.DeleteCampaign(campaignToDelete._id);
    const deleteWithMissions = deleteMissions === "delete";
    const r = await PerformRequest({
      method: ep.method,
      route: `${ep.route}?deleteMissions=${deleteWithMissions}`,
    });

    if (r.status === HttpCodes.Success) {
      reloadData();
      setDeleteModalOpen(false);
      setCampaignToDelete(null);
    } else {
      setErrorMessage("Failed to delete campaign");
      setErrorModalOpen(true);
    }
    setIsDeleting(false);
  };

  const openStatusModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setNewStatus(campaign.status);
    setStatusModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedCampaign || !newStatus) return;
    setIsUpdatingStatus(true);

    const ep = Endpoints.UpdateCampaign(selectedCampaign._id);
    const r = await PerformRequest({
      method: "PATCH",
      route: ep.route,
      data: { status: newStatus },
    });

    if (r.status === HttpCodes.Success) {
      reloadData();
      setStatusModalOpen(false);
    } else {
      setErrorMessage("Failed to update status");
      setErrorModalOpen(true);
    }
    setIsUpdatingStatus(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge color="green">Active</Badge>;
      case "completed":
        return <Badge color="green">Completed</Badge>;
      default:
        return <Badge color="gray">Draft</Badge>;
    }
  };

  const rows = campaigns.map((campaign: Campaign) => (
    <Table.Tr key={campaign._id}>
      <Table.Td>
        <Group gap="sm">
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              backgroundColor: campaign.branding?.primaryColor || "#ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {campaign.branding?.logoUrl ? (
              <img
                src={campaign.branding.logoUrl}
                alt=""
                style={{ width: 20, height: 20, objectFit: "contain" }}
              />
            ) : (
              <IconSpeakerphone size={16} color="white" />
            )}
          </div>
          <div>
            <Text fw={500} size="sm">
              {campaign.title}
            </Text>
            <Text size="xs" c="dimmed">
              {campaign.clientName}
            </Text>
            {campaign.isLocked && (
              <Badge color="red" variant="dot" size="xs" mt={4}>
                Locked
              </Badge>
            )}
            {campaign.squadsOnly && (
              <Badge color="yellow" variant="dot" size="xs" mt={4}>
                Squads Only
              </Badge>
            )}
          </div>
        </Group>
      </Table.Td>
      <Table.Td>{getStatusBadge(campaign.status)}</Table.Td>
      <Table.Td>
        <Text size="sm">
          {new Date(campaign.createdAt).toLocaleDateString()}
        </Text>
      </Table.Td>
      <Table.Td>
        <CampaignActions
          campaign={campaign}
          onDelete={openDeleteModal}
          onUpdateStatus={openStatusModal}
        />
      </Table.Td>
    </Table.Tr>
  ));

  const skeletonRows = Array.from({ length: 5 }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td>
        <Group gap="sm">
          <Skeleton height={32} width={32} radius="md" />
          <div>
            <Skeleton height={14} width={120} mb={4} />
            <Skeleton height={10} width={80} />
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Skeleton height={20} width={60} radius="xl" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={14} width={80} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={20} width={40} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Container size="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1}>Campaigns</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Manage branded campaigns
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate("/d/campaigns/new")}
          >
            Create Campaign
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group mb="md">
            <TextInput
              placeholder="Search campaigns..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setCurrentPage(1);
              }}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Status"
              data={[
                { value: "active", label: "Active" },
                { value: "draft", label: "Draft" },
                { value: "completed", label: "Completed" },
              ]}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
              }}
              clearable
              style={{ width: 150 }}
            />
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() => setExportModalOpen(true)}
            >
              Export Data
            </Button>
          </Group>

          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Campaign</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                skeletonRows
              ) : rows.length > 0 ? (
                rows
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={4} align="center">
                    No campaigns found
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>

          <Group justify="center" mt="md">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() => reloadData()}
            >
              Reload Campaigns
            </Button>
          </Group>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
                size="sm"
              />
            </Group>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title="Update Campaign Status"
        centered
      >
        <Stack>
          <Text size="sm">
            Change status for <b>{selectedCampaign?.title}</b>:
          </Text>
          <Select
            data={[
              { value: "draft", label: "Draft" },
              { value: "active", label: "Active" },
              { value: "completed", label: "Completed" },
            ]}
            value={newStatus}
            onChange={setNewStatus}
            allowDeselect={false}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setStatusModalOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={isUpdatingStatus}
              onClick={handleUpdateStatus}
              leftSection={<IconCheck size={16} />}
            >
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ExportModal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Export Campaigns"
        type="campaigns"
        additionalFilters={null}
        onExport={async (dateFilters) => {
          const token = localStorage.getItem("t");
          const endpoint = Endpoints.GetCampaignsReport({
            format: "csv",
            ...dateFilters,
          });

          const response = await axios.get(endpoint.route, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `campaigns_export_${new Date().toISOString()}.csv`,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCampaignToDelete(null);
        }}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="orange" />
            <Text fw={700}>Delete Campaign</Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text size="sm">
            You are about to delete <b>{campaignToDelete?.title}</b>. This
            action cannot be undone.
          </Text>

          <Text size="sm" fw={500}>
            What should happen to the opportunities under this campaign?
          </Text>

          <Radio.Group value={deleteMissions} onChange={setDeleteMissions}>
            <Stack gap="xs">
              <Radio
                value="keep"
                label="Keep opportunities (unlink from campaign)"
                description="Opportunities will remain but won't be associated with any campaign"
              />
              <Radio
                value="delete"
                label="Delete all opportunities"
                description="Permanently delete all opportunities under this campaign"
                color="red"
              />
            </Stack>
          </Radio.Group>

          {deleteMissions === "delete" && (
            <Paper
              p="sm"
              bg="red.0"
              radius="md"
              withBorder
              style={{ borderColor: "var(--mantine-color-red-3)" }}
            >
              <Group gap="xs">
                <IconAlertTriangle size={16} color="red" />
                <Text size="xs" c="red.7" fw={500}>
                  Warning: This will permanently delete all opportunities and
                  their submissions!
                </Text>
              </Group>
            </Paper>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpen(false);
                setCampaignToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={isDeleting}
              onClick={confirmDelete}
              leftSection={<IconTrash size={16} />}
            >
              Delete Campaign
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Error Modal */}
      <Modal
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={
          <Group gap="xs">
            <IconX size={20} color="red" />
            <Text fw={700} c="red">
              Error
            </Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text>{errorMessage}</Text>
          <Group justify="flex-end">
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default Campaigns;
